"use client";
import { useState } from "react";
import { ChevronRight, Scan } from "react-iconly";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import BankCardIcon from "@/assets/icons/bank-card.svg";
import { EmptyResult, LineItem } from "@/components/partials";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { useCart } from "@/context";
import { QpayInvoiceType } from "@/lib/schema";
import { currencyFormat } from "@/lib/utils";

import { checkOrder, createOrder } from "./action";

// zawaarsan ghdee hangalttai
export default function CartPageClient() {
  const router = useRouter();
  const [qpay, setQpay] = useState<QpayInvoiceType | null>(null);
  const [loading, setLoading] = useState<"0" | "1" | "check" | null>(null);
  const { items, clearCart, totalPrice } = useCart();

  const handleOrder = (
    type: "0" | "1",
    cb: (data: QpayInvoiceType | { url: string }) => void
  ) => {
    setLoading(type);
    createOrder(
      items.map((c) => c.productId),
      type
    )
      .then((c) => {
        if (c.data) return cb(c.data?.data);
        toast.error("Банктай холбогдоход алдаа гарлаа", {
          position: "top-right",
        });
      })
      .finally(() => setLoading(null));
  };

  if (!items?.length)
    return <EmptyResult label="Бүтээгдэхүүн сагслаагүй байна" />;
  return (
    <>
      <div className="mx-auto my-24  max-md:my-10 w-[450px] max-w-full max-md:px-4">
        <h1 className="text-xl mb-4 font-bold max-md:text-xl md:hidden">
          Таны сагс
        </h1>
        <div className="md:hidden mb-4">
          {items.map((c, idx) => (
            <LineItem
              key={idx}
              price={c.price}
              productType={c.productType}
              productId={c.productId}
              title={c.productName}
              thumbnail={c.thumbImg}
              loading={c.adding || c.removing}
            />
          ))}
        </div>
        <div className="flex items-center justify-between gap-1 md:hidden mb-16">
          <span className="text-[#84807D] text-sm">Нийт төлөх дүн:</span>
          <span className="text-primary text-sm font-bold">
            {currencyFormat(totalPrice)}
          </span>
        </div>
        <h1 className="text-4xl mb-16 font-bold text-center max-md:mb-4 max-md:text-xl max-md:text-left">
          Төлөх хэлбэр сонгох
        </h1>
        <button
          disabled={loading === "0"}
          className="bg-[#FBF9F8] rounded-md p-5 flex items-center gap-5 text-left w-full mb-3 max-md:p-3"
          onClick={() =>
            handleOrder("0", (data) => setQpay(data as QpayInvoiceType))
          }
        >
          <Scan primaryColor="#F96F5D" />
          <span className="flex-1">Qpay</span>
          {loading === "0" ? (
            <Loader2 className="w-6 h-6 animate-spin text-slate-200" />
          ) : (
            <ChevronRight size={20} />
          )}
        </button>
        <button
          disabled={loading === "1"}
          className="bg-[#FBF9F8] rounded-md p-5 flex items-center gap-5 text-left w-full mb-3 max-md:p-3"
          onClick={() =>
            handleOrder("1", (data) =>
              window.location.assign((data as { url: string })?.url as string)
            )
          }
        >
          <BankCardIcon style={{ width: 24, height: 24, color: "#F96F5D" }} />
          <span className="flex-1">Карт</span>
          {loading === "1" ? (
            <Loader2 className="w-6 h-6 animate-spin text-slate-200" />
          ) : (
            <ChevronRight size={20} />
          )}
        </button>
      </div>
      <Drawer open={!!qpay} onClose={() => setQpay(null)}>
        <DrawerContent>
          <div className="mx-auto w-full max-w-sm text-center max-h-[90dvh] overflow-y-auto py-16">
            {qpay?.qr.base64 && (
              <img
                src={`data:image/jpg;base64,${qpay?.qr.base64}`}
                alt=""
                className="w-2/3 mx-auto"
              />
            )}
            {qpay?.invoiceId && (
              <Button
                className="mt-4"
                size="lg"
                type="button"
                disabled={loading === "check"}
                onClick={() => {
                  setLoading("check");
                  checkOrder(qpay?.invoiceId)
                    .then((c) => {
                      if (c.data?.message) {
                        clearCart();
                        router.replace("/account");
                      }
                      toast.info(c.data?.message ?? c.error);
                    })
                    .finally(() => setLoading(null));
                }}
              >
                {loading === "check" && (
                  <Loader2 className="w-4 h-4 animate-spin text-primary-foreground mr-2" />
                )}
                Төлбөр шалгах
              </Button>
            )}
            <div className="md:hidden text-center">
              <h1 className="text-4xl my-16 font-bold max-md:mb-8 max-md:text-xl">
                Банк сонгох
              </h1>
              <div className="grid grid-cols-3 gap-7 max-md:gap-4">
                {qpay?.deeplinks?.map((c, idx) => (
                  <a
                    key={idx}
                    href={c.link}
                    className="px-4 py-3 flex flex-col items-center justify-center gap-3 bg-[#FBF9F8] rounded-md hover:bg-[#f5f3f2] max-md:text-xs"
                  >
                    <img src={c.logo} alt={c.name} width={34} height={34} />
                    <span className="truncate max-w-full">{c.description}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
